import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  createApiToken,
  deleteApiToken,
  fetchApiTokens,
  type ApiTokenData,
} from '../../lib/api';

function formatDate(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleString();
}

export default function ApiTokensSection() {
  const [tokens, setTokens] = useState<ApiTokenData[] | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedToken, setRevealedToken] = useState<{ name: string; token: string } | null>(null);

  useEffect(() => {
    fetchApiTokens()
      .then(setTokens)
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to load tokens'));
  }, []);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const result = await createApiToken(name);
      setTokens((prev) => [
        { id: result.id, name: result.name, keyPrefix: result.keyPrefix, created: result.created, lastUsed: result.lastUsed },
        ...(prev ?? []),
      ]);
      setRevealedToken({ name: result.name, token: result.token });
      setNewName('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token: ApiTokenData) => {
    if (!window.confirm(`Revoke token "${token.name}"? This cannot be undone.`)) return;
    try {
      await deleteApiToken(token.id);
      setTokens((prev) => (prev ?? []).filter((t) => t.id !== token.id));
      toast.success('Token revoked');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke token');
    }
  };

  const handleCopy = () => {
    if (!revealedToken) return;
    void navigator.clipboard.writeText(revealedToken.token);
    toast.success('Copied to clipboard');
  };

  return (
    <section className="malpedia-settings-section">
      <h2 className="malpedia-settings-section-title">API Tokens</h2>
      <p className="malpedia-settings-hint">
        Used to authenticate the Obsidian plugin and other API clients via{' '}
        <code>Authorization: Token &lt;key&gt;</code>.
      </p>

      {revealedToken && (
        <div className="malpedia-settings-test-banner malpedia-token-reveal-banner">
          <p>
            Token <strong>{revealedToken.name}</strong> created. Copy it now &mdash;{' '}
            <strong>it will not be shown again</strong>.
          </p>
          <div className="malpedia-token-reveal-row">
            <code className="malpedia-token-reveal-value">{revealedToken.token}</code>
            <button type="button" className="malpedia-settings-eye-btn" onClick={handleCopy}>
              Copy
            </button>
          </div>
          <button
            type="button"
            className="malpedia-token-reveal-dismiss"
            onClick={() => setRevealedToken(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {tokens === null ? (
        <p className="malpedia-settings-hint">Loading tokens...</p>
      ) : tokens.length === 0 ? (
        <p className="malpedia-settings-hint">No API tokens yet.</p>
      ) : (
        <div className="malpedia-token-list">
          {tokens.map((token) => (
            <div className="malpedia-settings-row malpedia-token-row" key={token.id}>
              <div className="malpedia-token-info">
                <span className="malpedia-token-name">{token.name}</span>
                <span className="malpedia-token-meta">
                  {token.keyPrefix}&hellip; &middot; created {formatDate(token.created)} &middot; last used{' '}
                  {formatDate(token.lastUsed)}
                </span>
              </div>
              <button
                type="button"
                className="malpedia-token-revoke-btn"
                onClick={() => handleRevoke(token)}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="malpedia-settings-row malpedia-token-new-row">
        <input
          type="text"
          className="malpedia-settings-input"
          placeholder="Token name, e.g. obsidian-laptop"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
          maxLength={100}
        />
        <button
          type="button"
          className="malpedia-settings-fetch-btn malpedia-token-create-btn"
          onClick={handleCreate}
          disabled={creating || !newName.trim()}
        >
          {creating ? 'Creating...' : 'Create'}
        </button>
      </div>
    </section>
  );
}
